import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Plus, X, Link, ArrowLeft, Trash2, PanelRightOpen, ArrowRightLeft } from 'lucide-react';
import { ProjectSection } from '../components/ProjectSection';
import { InfoTip } from '../components/InfoTip';
import { DndContext, DragOverlay, useDraggable, useDroppable, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { ConfirmModal } from '../components/ConfirmModal';
import type { DragStartEvent, DragEndEvent } from '@dnd-kit/core';
import { useT } from '../i18n';
import { useAuth } from '../context/AuthContext';
import { useRole } from '../context/RoleContext';
import { api } from '../api/client';
import { TaskCard, type TaskLinkInfo } from '../components/TaskCard';
import { TaskDetailPanel } from '../components/TaskDetailPanel';
import { TaskTimeline } from '../components/TaskTimeline';
import { DropdownSelect } from '../components/DropdownSelect';
import { DateHistory, formatPlannedSlot } from '../components/DateHistory';
import { ContactTaskCreator } from '../components/ContactTaskCreator';
import { RemindTaskAssignee } from '../components/RemindTaskAssignee';
import { TaskPrivacyField } from '../components/TaskPrivacyField';
import { MilestoneDetailPanel, type MilestonePanelTask } from '../components/MilestoneDetailPanel';
import { TaskTodoList } from '../proposals/vue-jalons-taches/TaskTodoList';
import { TaskCollaboratorsField, ProjectPeopleHint, projectPeople } from '../proposals/collaborateurs-tache/TaskCollaboratorsField';
import { DocumentsList } from '../proposals/documents/DocumentsList';
import { SearchableSelect } from '../components/SearchableSelect';
import { MigrateTaskModal, canMigrateTask } from '../components/MigrateTaskModal';
import { MultiSelectFilter } from '../components/MultiSelectFilter';
import { TaskFilters, useProjectFilter, usePersonFilter } from '../components/TaskFilters';
import { FilterToggle } from '../components/FilterSelect';
import { usePageFilters } from '../context/PageFiltersContext';
import { BareTextarea, getMilestoneOptions, getMilestoneDueMap, clampDueToMilestone, formatMilestoneDueDate } from '../components/ProjectForm';
import { getDoneStatusIds, getClosedColumnIds, getInboxStatusIds, getBlockedColumnIds } from '../utils/stages';
import { projectDisplayCode, taskDisplayCode } from '../utils/identifiers';
import { NoMilestonesNotice, MissingMilestoneNotice, taskSaveErrorMessage } from '../components/TaskMilestoneNotice';
import type { Task, TaskDetail, TaskDependency, GridColumn, GridRow, KanbanProject, KanbanBoard, Project, Collaborator } from '../types';
import { usePresence, useLiveChanges, setLiveFocus, focusMap, flashMoved, draggingOver, type LivePerson } from '../live/live';
import { PresenceAvatars, FocusBadge, LiveGhost, LiveCursors } from '../live/Presence';

// ── JSON helper ──

function parseJSON<T>(val: any, fallback: T): T {
  if (!val) return fallback;
  if (typeof val === 'object') return val;
  try { return JSON.parse(val); } catch { return fallback; }
}

// planned_at is a TIMESTAMPTZ string ("YYYY-MM-DD ..."); trim to a date-input value.
function plannedAtToDateInput(iso: string | null | undefined): string {
  return iso ? iso.slice(0, 10) : '';
}

// A timestamp → its local day ("YYYY-MM-DD"), e.g. as the earliest due date a
// task can get: a due date can't come before the task's creation.
function localDayOf(value: string | Date): string {
  const d = new Date(value);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

// A date-input value ("YYYY-MM-DD") → midnight ISO for the planned_at column.
function dateInputToPlannedAt(date: string): string | null {
  return date ? `${date}T00:00:00` : null;
}

// Like dateInputToPlannedAt, but keeps the time of an existing planned slot
// (set in the calendar planner) — this page only edits the date, and it resends
// planned_at on every save, so a midnight fallback would wipe the slot's time.
// Same date → the stored value untouched; new date → that date at the old
// wall-clock time, left without an offset so Postgres applies the right one.
function dateInputToPlannedAtKeepingTime(date: string, existing: string | null | undefined): string | null {
  if (!date || !existing) return dateInputToPlannedAt(date);
  if (plannedAtToDateInput(existing) === date) return existing;
  return `${date}${existing.slice(10, 19)}`;
}

// ── Draggable Task Card ──

function DraggableTaskCard({ task, locked, dimmed, linkInfo, onTitleClick, onInfo, isBlocked, onBlockedReasonChange, liveBy }: { task: Task; /** Other people moving it or with it open right now. */ liveBy?: LivePerson[]; /** Read-only copy on a linked (non-home) row, or a private task this user can't open — not draggable from here. */ locked?: boolean; dimmed?: boolean; linkInfo?: TaskLinkInfo; onTitleClick: () => void; onInfo: (e: React.MouseEvent) => void; isBlocked?: boolean; onBlockedReasonChange?: (reason: string) => void }) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: `task-${task.id}`,
    data: { task },
    disabled: locked,
  });

  return (
    <div
      ref={setNodeRef}
      {...(locked ? {} : { ...listeners, ...attributes })}
      data-dnd-draggable
      data-dnd-dragging={isDragging || undefined}
      style={{ opacity: isDragging ? 0.4 : 1, position: 'relative' }}
    >
      <TaskCard task={task} dimmed={dimmed} linkInfo={linkInfo} onTitleClick={onTitleClick} onInfo={onInfo} isBlocked={isBlocked} onBlockedReasonChange={onBlockedReasonChange} />
      <FocusBadge people={liveBy} />
    </div>
  );
}

// ── Droppable Cell ──

function DroppableCell({ id, children }: { id: string; children: React.ReactNode }) {
  const { setNodeRef, isOver } = useDroppable({ id });

  return (
    <div
      ref={setNodeRef}
      className="matrix-grid-cell"
      data-dnd-over={isOver || undefined}
      data-live-anchor={id} // live pointers show only over the task cells, placed relative to the cell
      style={{ minHeight: 250 }}
    >
      {children}
    </div>
  );
}

// ── Create Task Modal ──

function CreateTaskModal({
  columns,
  projects,
  defaultProjectId,
  defaultColumnId,
  onClose,
  onCreated,
}: {
  columns: GridColumn[];
  projects: Project[];
  defaultProjectId?: number;
  defaultColumnId?: number;
  onClose: () => void;
  onCreated: () => void;
}) {
  const { t } = useT();
  const { isAdmin } = useRole();
  const [projectId, setProjectId] = useState<number>(defaultProjectId || 0);
  const [columnId, setColumnId] = useState<number>(defaultColumnId || columns[0]?.id || 0);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [plannedDate, setPlannedDate] = useState('');
  const [assigneeName, setAssigneeName] = useState('');
  const [collaboratorNames, setCollaboratorNames] = useState<string[]>([]);
  const [milestoneId, setMilestoneId] = useState('');
  const [linkedProjectIds, setLinkedProjectIds] = useState<number[]>([]);
  const [creating, setCreating] = useState(false);

  // Dependencies
  const [projectTasks, setProjectTasks] = useState<{ id: number; title: string; column_label: string }[]>([]);
  const [dependsOn, setDependsOn] = useState<{ id: number; title: string }[]>([]);
  const [enables, setEnables] = useState<{ id: number; title: string }[]>([]);
  const [showDepPicker, setShowDepPicker] = useState<'depends_on' | 'enables' | null>(null);

  // Derive assignee options from selected project's collaborators + sponsor
  const assigneeOptions = useMemo(() => {
    if (!projectId) return [];
    const project = projects.find(p => p.id === projectId);
    if (!project) return [];
    const opts: { value: string; label: string }[] = [];
    const ownership = parseJSON<any>(project.ownership, {});
    if (ownership.sponsor) {
      opts.push({ value: ownership.sponsor, label: t('tasks.sponsorOption', { name: ownership.sponsor }) });
    }
    const re = parseJSON<any>(project.resources_effort, {});
    const collabs: Collaborator[] = re.collaborators || [];
    for (const c of collabs) {
      if (c.name && !opts.some(o => o.value === c.name)) {
        opts.push({ value: c.name, label: c.name });
      }
    }
    return opts;
  }, [projectId, projects, t]);

  // Milestones defined on the selected project (Planning stage)
  const selectedProject = useMemo(() => projects.find(p => p.id === projectId), [projectId, projects]);
  const milestoneOptions = useMemo(() => selectedProject ? getMilestoneOptions(selectedProject.planning, t('common.dateFormatPlaceholder'), t('taskDetail.milestoneDuePrefix')) : [], [selectedProject, t]);
  const milestoneDueMap = useMemo(() => selectedProject ? getMilestoneDueMap(selectedProject.planning) : new Map<string, string>(), [selectedProject]);

  // Linking a milestone pulls the due date back to the milestone's estimated end
  // (adopted when unset, clamped when later; left as-is otherwise) — for
  // everyone, not just admins (issue #36). Typing a due date directly is still
  // PMO/admin-only (issue #30) — except for a task without a milestone, which
  // has nothing to derive it from; the server re-derives it from the milestone
  // for non-admins regardless of what this optimistic client value ends up being.
  const selectMilestone = (id: string) => {
    setMilestoneId(id);
    setDueDate(prev => clampDueToMilestone(prev, id ? (milestoneDueMap.get(id) || '') : ''));
  };

  // Load project tasks for dependency picker when project changes
  useEffect(() => {
    setAssigneeName('');
    setCollaboratorNames([]);
    setMilestoneId('');
    setLinkedProjectIds([]);
    setDependsOn([]);
    setEnables([]);
    setProjectTasks([]);
    if (projectId) {
      api.getProjectTasks(projectId).then((tasks) => setProjectTasks(tasks as { id: number; title: string; column_label: string }[]));
    }
  }, [projectId]);

  // Available tasks (exclude already selected)
  const availableDepTasks = useMemo(() => {
    const selectedIds = new Set([...dependsOn.map(d => d.id), ...enables.map(d => d.id)]);
    return projectTasks.filter(t => !selectedIds.has(t.id));
  }, [projectTasks, dependsOn, enables]);

  const handleCreate = async () => {
    if (!projectId || !columnId || !title.trim()) return;
    setCreating(true);
    try {
      const newTask = await api.createTask({
        project_id: projectId,
        column_id: columnId,
        title: title.trim(),
        description: description || undefined,
        due_date: dueDate || undefined,
        planned_at: dateInputToPlannedAt(plannedDate) || undefined,
        assignee_name: assigneeName || undefined,
        collaborator_names: collaboratorNames.length > 0 ? collaboratorNames : undefined,
        milestone_id: milestoneId || undefined,
        linked_project_ids: linkedProjectIds.length > 0 ? linkedProjectIds : undefined,
      }) as { id: number };

      // Add dependencies after task creation
      for (const dep of dependsOn) {
        await api.addTaskDependency(newTask.id, dep.id);
      }
      for (const dep of enables) {
        await api.addTaskDependency(dep.id, newTask.id);
      }

      onCreated();
      onClose();
    } catch (err: any) {
      alert(taskSaveErrorMessage(err, t));
    } finally {
      setCreating(false);
    }
  };

  // Labeled with the project number so typing e.g. "55" finds project AE55
  // directly, rather than requiring the exact name (issue: 50+ projects).
  const projectOptions = projects.map(p => ({ value: p.id, label: `${projectDisplayCode(p).toUpperCase()} — ${p.name}` }));
  const columnOptions = columns.map(c => ({ value: c.id, label: c.label }));
  // A task may also belong to any number of other projects — shown/editable
  // from those projects' boards too. Offered by number + name; excludes the
  // primary project.
  const linkedProjectOptions = useMemo(
    () => projects.filter(p => p.id !== projectId).map(p => ({ value: p.id, label: `${projectDisplayCode(p).toUpperCase()} — ${p.name}` })),
    [projects, projectId]
  );

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 560 }}>
        <div className="modal-header">
          <h3 className="modal-title">{t('tasks.createTask')}</h3>
          <button className="close-btn" onClick={onClose}><X size={16} /></button>
        </div>
        <div className="modal-body">
          <div className="create-task-form">
            <div className="form-group">
              <label className="form-label">{t('tasks.project')}</label>
              <SearchableSelect
                value={projectId}
                options={projectOptions}
                placeholder={t('tasks.selectProjectPlaceholder')}
                onChange={(v) => setProjectId(Number(v))}
              />
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--s12)' }}>
              <div className="form-group">
                <label className="form-label">{t('tasks.statusColumn')}</label>
                <SearchableSelect
                  value={columnId}
                  options={columnOptions}
                  onChange={(v) => setColumnId(Number(v))}
                />
              </div>
              <div className="form-group">
                <label className="form-label">{t('tasks.assignee')}</label>
                <SearchableSelect
                  value={assigneeName}
                  options={assigneeOptions}
                  placeholder={t('tasks.unassigned')}
                  onChange={(v) => setAssigneeName(String(v))}
                />
              </div>
            </div>
            {projectId > 0 && (
              <div className="form-group">
                <label className="form-label form-label--tip">{t('collab.label')} <ProjectPeopleHint projectId={projectId} /></label>
                <TaskCollaboratorsField
                  people={projectPeople(projects.find(p => p.id === projectId))}
                  assignee={assigneeName}
                  value={collaboratorNames}
                  onChange={setCollaboratorNames}
                />
              </div>
            )}
            <div className="form-group">
              <label className="form-label">{t('common.title')}</label>
              <input className="form-input" value={title} onChange={(e) => setTitle(e.target.value)} placeholder={t('tasks.titlePlaceholder')} autoFocus />
            </div>
            <div className="form-group">
              <label className="form-label">{t('common.description')}</label>
              <BareTextarea value={description} onChange={setDescription} placeholder={t('tasks.descriptionPlaceholder')} />
            </div>
            {/* The milestone is optional; a task without one says so. It comes
                before the dates: choosing one fills the due date with its end. */}
            {projectId > 0 && (milestoneOptions.length > 0 ? (
              <div className="form-group">
                <label className="form-label">{t('tasks.milestone')}</label>
                <DropdownSelect
                  value={milestoneId}
                  options={milestoneOptions}
                  placeholder={t('tasks.chooseMilestone')}
                  onChange={(v) => selectMilestone(String(v))}
                />
                {!milestoneId && <MissingMilestoneNotice />}
              </div>
            ) : (
              <NoMilestonesNotice projectId={projectId} />
            ))}

            {/* [Proposition: timeline-taches] Both dates are required to create a task. */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--s12)' }}>
              <div className="form-group">
                <label className="form-label">{t('tasks.dueDate')} *</label>
                <input
                  className="form-input"
                  type="date"
                  value={dueDate}
                  min={localDayOf(new Date())}
                  onChange={(e) => setDueDate(e.target.value)}
                  disabled={!isAdmin && !!milestoneId}
                  title={isAdmin || !milestoneId ? undefined : t('tasks.dueDateAdminOnly')}
                />
                {!isAdmin && milestoneId && !dueDate && <div className="text-sm text-ink-3" style={{ marginTop: 4 }}>{t('tlt.req.milestoneNoEnd')}</div>}
              </div>
              <div className="form-group">
                <label className="form-label">{t('tasks.plannedDate')} *</label>
                <input className="form-input" type="date" value={plannedDate} onChange={(e) => setPlannedDate(e.target.value)} />
              </div>
            </div>

            {projectId > 0 && (
              <div className="form-group">
                <label className="form-label">{t('tasks.linkedProject')}</label>
                <div className="linked-projects-field">
                  <MultiSelectFilter
                    label=""
                    options={linkedProjectOptions}
                    selected={linkedProjectIds}
                    onChange={setLinkedProjectIds}
                    placeholder={t('tasks.linkedProjectPlaceholder')}
                  />
                </div>
              </div>
            )}

            {/* Dependencies section */}
            {projectId > 0 && (
              <div className="form-group">
                <label className="form-label">{t('tasks.dependencies')}</label>
                <div className="task-detail-deps">
                  <div className="task-detail-dep-group">
                    <span className="task-detail-meta-label">{t('tasks.dependentOn')}</span>
                    {dependsOn.length === 0 ? (
                      <div style={{ fontSize: 'var(--t-12)', color: 'var(--ink-3)' }}>{t('common.none')}</div>
                    ) : (
                      <div className="task-dep-list">
                        {dependsOn.map(d => (
                          <div key={d.id} className="task-dep-item">
                            <span className="task-dep-item-title">{d.title}</span>
                            <button className="btn btn-ghost btn-sm" style={{ padding: '2px' }}
                              onClick={() => setDependsOn(prev => prev.filter(x => x.id !== d.id))}>
                              <X size={12} />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                    <button className="btn btn-ghost btn-sm" onClick={() => setShowDepPicker('depends_on')}>
                      <Plus size={12} /> {t('common.add')}
                    </button>
                  </div>
                  <div className="task-detail-dep-group">
                    <span className="task-detail-meta-label">{t('tasks.enables')}</span>
                    {enables.length === 0 ? (
                      <div style={{ fontSize: 'var(--t-12)', color: 'var(--ink-3)' }}>{t('common.none')}</div>
                    ) : (
                      <div className="task-dep-list">
                        {enables.map(d => (
                          <div key={d.id} className="task-dep-item">
                            <span className="task-dep-item-title">{d.title}</span>
                            <button className="btn btn-ghost btn-sm" style={{ padding: '2px' }}
                              onClick={() => setEnables(prev => prev.filter(x => x.id !== d.id))}>
                              <X size={12} />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                    <button className="btn btn-ghost btn-sm" onClick={() => setShowDepPicker('enables')}>
                      <Plus size={12} /> {t('common.add')}
                    </button>
                  </div>
                </div>

                {showDepPicker && (
                  <div className="task-dep-picker" style={{ marginTop: 'var(--s8)' }}>
                    <div className="task-dep-picker-title">
                      {showDepPicker === 'depends_on' ? t('tasks.selectDependsOn') : t('tasks.selectEnables')}
                    </div>
                    {availableDepTasks.length > 0 ? (
                      <div className="task-dep-picker-list">
                        {availableDepTasks.map(depTask => (
                          <button key={depTask.id} className="task-dep-picker-item" onClick={() => {
                            if (showDepPicker === 'depends_on') {
                              setDependsOn(prev => [...prev, { id: depTask.id, title: depTask.title }]);
                            } else {
                              setEnables(prev => [...prev, { id: depTask.id, title: depTask.title }]);
                            }
                            setShowDepPicker(null);
                          }}>
                            <Link size={12} />
                            <span style={{ flex: 1 }}>{depTask.title}</span>
                            <span className="task-dep-picker-item-status">{depTask.column_label}</span>
                          </button>
                        ))}
                      </div>
                    ) : (
                      <div style={{ fontSize: 'var(--t-12)', color: 'var(--ink-3)' }}>{t('tasks.noAvailableTasks')}</div>
                    )}
                    <button className="btn btn-ghost btn-sm" onClick={() => setShowDepPicker(null)}>{t('common.cancel')}</button>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-ghost" onClick={onClose}>{t('common.cancel')}</button>
          <button className="btn btn-accent" disabled={creating || !projectId || !title.trim() || !plannedDate || !dueDate}
            title={(!plannedDate || !dueDate) ? t('tlt.req.dates') : undefined} onClick={handleCreate}>
            {t('tasks.createTask')}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Full Task Detail View (replaces board) ──

function TaskDetailView({
  taskId,
  currentUserId,
  columns,
  projects,
  migrateTargets,
  contextProjectId,
  onBack,
  onDeleted,
}: {
  taskId: number;
  currentUserId: number;
  columns: GridColumn[];
  projects: Project[];
  /** Projects the task may be migrated to (same rules as task creation). */
  migrateTargets: Project[];
  /** The board row (project) this view was opened from — lets the "linked
   *  project" indicator show whichever of the task's two projects isn't this one. */
  contextProjectId?: number | null;
  onBack: () => void;
  onDeleted: () => void;
}) {
  const { t } = useT();
  const { isAdmin, canSeeProposals } = useRole();
  const { user } = useAuth();
  const [task, setTask] = useState<TaskDetail | null>(null);
  const [privateLocked, setPrivateLocked] = useState(false);
  const [loading, setLoading] = useState(true);

  // Editable fields
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [plannedDate, setPlannedDate] = useState('');
  const [assigneeName, setAssigneeName] = useState('');
  const [privateNotes, setPrivateNotes] = useState('');
  const [milestoneId, setMilestoneId] = useState('');
  const [linkedProjectIds, setLinkedProjectIds] = useState<number[]>([]);
  const [columnId, setColumnId] = useState<number>(0);

  // Dependency add
  const [showDepPicker, setShowDepPicker] = useState<'depends_on' | 'enables' | null>(null);
  const [projectTasks, setProjectTasks] = useState<{ id: number; title: string; column_label: string }[]>([]);
  const [newDepTitle, setNewDepTitle] = useState('');
  const [newDepPlanned, setNewDepPlanned] = useState(''); // [Proposition: timeline-taches] required
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [showMigrate, setShowMigrate] = useState(false);
  // To-do progress, shown in the to-do card's title (reported by the list itself).
  const [todoCount, setTodoCount] = useState<{ done: number; total: number } | null>(null);

  const loadTask = useCallback(async () => {
    try {
      const data = await api.getTaskDetail(taskId) as TaskDetail;
      setTask(data);
      setTitle(data.title);
      setDescription(data.description || '');
      setDueDate(data.due_date || '');
      setPlannedDate(plannedAtToDateInput(data.planned_at));
      setAssigneeName(data.assignee_name || '');
      setPrivateNotes(data.private_notes || '');
      setMilestoneId(data.milestone_id || '');
      setLinkedProjectIds(data.linked_projects.map((lp) => lp.id));
      setColumnId(data.column_id);
    } catch (err: any) {
      // task not found — or private and not ours to open
      if (err?.code === 'TASK_PRIVATE') setPrivateLocked(true);
    } finally {
      setLoading(false);
    }
  }, [taskId]);

  useEffect(() => { loadTask(); }, [loadTask]);

  const save = async () => {
    if (!task) return;
    await api.updateTask(task.id, {
      title: title.trim() || task.title,
      description: description || null,
      due_date: dueDate || null,
      planned_at: dateInputToPlannedAtKeepingTime(plannedDate, task.planned_at),
      assignee_name: assigneeName || null,
      private_notes: privateNotes || null,
      milestone_id: milestoneId || null,
      linked_project_ids: linkedProjectIds,
    });
    loadTask();
  };

  // The picker only shows/edits links *other than* whichever project this
  // view was opened from — that one is implicit (it's why the task is here
  // at all), so editedIds excludes it and must be re-added before persisting.
  const changeLinkedProjects = async (editedIds: number[]) => {
    if (!task) return;
    const viewContext = contextProjectId ?? task.project_id;
    const fullIds = viewContext !== task.project_id ? [...editedIds, viewContext] : editedIds;
    setLinkedProjectIds(fullIds);
    await api.updateTask(task.id, {
      title: title.trim() || task.title,
      description: description || null,
      due_date: dueDate || null,
      planned_at: dateInputToPlannedAtKeepingTime(plannedDate, task.planned_at),
      assignee_name: assigneeName || null,
      private_notes: privateNotes || null,
      milestone_id: milestoneId || null,
      linked_project_ids: fullIds,
    });
    loadTask();
  };

  const handleColumnChange = async (newColId: number) => {
    if (!task || newColId === task.column_id) return;
    setColumnId(newColId);
    try {
      await api.moveTask(task.id, newColId);
    } catch (err: any) {
      // [Proposition: vue-jalons-taches] Done refused while the to-do list isn't fully checked.
      alert(/to-do/i.test(err.message) ? t('vjt.task.doneRefused') : taskSaveErrorMessage(err, t));
    }
    loadTask();
  };

  const handleDelete = async () => {
    if (!task) return;
    setConfirmDelete(true);
  };

  const handleDeleteConfirm = async () => {
    if (!task) return;
    setConfirmDelete(false);
    await api.deleteTask(task.id);
    onDeleted();
  };

  // Milestone side panel — opened from the icon next to the milestone picker.
  // The project's tasks are fetched on open so its "open tasks" list is fresh;
  // planning comes from the task detail (the milestone as currently saved).
  const [milestonePanelTasks, setMilestonePanelTasks] = useState<MilestonePanelTask[] | null>(null);
  const doneColumnIds = useMemo(() => getDoneStatusIds(columns), [columns]);
  const blockedColumnIds = useMemo(() => getBlockedColumnIds(columns), [columns]);
  const closedColumnIds = useMemo(() => getClosedColumnIds(columns), [columns]);
  const milestoneProject = useMemo(() => {
    const p = projects.find((x) => x.id === task?.project_id);
    return {
      id: task?.project_id ?? 0,
      name: task?.project_name ?? '',
      project_code: task?.project_code ?? null,
      planning: task?.planning ?? null,
      deliverables: p?.deliverables ?? null,
    };
  }, [projects, task]);

  const openMilestonePanel = async () => {
    if (!task) return;
    try {
      setMilestonePanelTasks(await api.getProjectTasks(task.project_id) as MilestonePanelTask[]);
    } catch {
      setMilestonePanelTasks([]);
    }
  };

  const openDepPicker = async (type: 'depends_on' | 'enables') => {
    if (!task) return;
    const tasks = await api.getProjectTasks(task.project_id) as { id: number; title: string; column_label: string }[];
    const existingIds = new Set([
      task.id,
      ...task.depends_on.map((d: TaskDependency) => d.id),
      ...task.enables.map((d: TaskDependency) => d.id),
    ]);
    setProjectTasks(tasks.filter(t => !existingIds.has(t.id)));
    setShowDepPicker(type);
  };

  const addDep = async (depTaskId: number) => {
    if (!task) return;
    if (showDepPicker === 'depends_on') {
      await api.addTaskDependency(task.id, depTaskId);
    } else {
      await api.addTaskDependency(depTaskId, task.id);
    }
    setShowDepPicker(null);
    loadTask();
  };

  const createAndAddDep = async () => {
    if (!task || !newDepTitle.trim() || !newDepPlanned) return;
    const cols = await api.getKanbanColumns() as { id: number }[];
    if (cols.length === 0) return;
    // The new task goes into this task's milestone; without one, it takes this
    // task's due date (a task needs one at creation).
    let newTask: { id: number };
    try {
      newTask = await api.createTask({
        project_id: task.project_id,
        column_id: cols[0].id,
        title: newDepTitle.trim(),
        milestone_id: task.milestone_id || undefined,
        due_date: task.milestone_id ? undefined : (task.due_date || undefined),
        planned_at: dateInputToPlannedAt(newDepPlanned) || undefined,
      }) as { id: number };
    } catch (err: any) {
      alert(taskSaveErrorMessage(err, t));
      return;
    }
    if (showDepPicker === 'depends_on') {
      await api.addTaskDependency(task.id, newTask.id);
    } else {
      await api.addTaskDependency(newTask.id, task.id);
    }
    setNewDepTitle('');
    setNewDepPlanned('');
    setShowDepPicker(null);
    loadTask();
  };

  const removeDep = async (depId: number) => {
    await api.removeTaskDependency(taskId, depId);
    loadTask();
  };

  const postUpdate = async (content: string, type: 'comment' | 'question' | 'reply', replyToId?: number, taggedUserIds?: number[]): Promise<number | void> => {
    const result: any = await api.postTaskUpdate(taskId, content, type, replyToId, taggedUserIds);
    return result?.id;
  };

  const editUpdate = async (updateId: number, content: string) => {
    await api.editTaskUpdate(taskId, updateId, content);
    loadTask();
  };

  const deleteUpdate = async (updateId: number) => {
    await api.deleteTaskUpdate(taskId, updateId);
    loadTask();
  };

  // Collaborators for assignee dropdown
  const collaborators: Collaborator[] = task ? parseJSON(task.resources_effort, { collaborators: [] }).collaborators || [] : [];
  const sponsor = task ? parseJSON(task.ownership, { sponsor: '' }).sponsor : '';
  const assigneeOptions = useMemo(() => {
    const opts: { value: string; label: string }[] = [];
    if (sponsor) opts.push({ value: sponsor, label: t('tasks.sponsorOption', { name: sponsor }) });
    for (const c of collaborators) {
      if (c.name !== sponsor) opts.push({ value: c.name, label: c.name });
    }
    return opts;
  }, [sponsor, collaborators, t]);

  const columnOptions = useMemo(() =>
    columns.map(c => ({ value: c.id, label: c.label })),
    [columns]
  );

  const milestoneOptions = useMemo(() => task ? getMilestoneOptions(task.planning, t('common.dateFormatPlaceholder'), t('taskDetail.milestoneDuePrefix')) : [], [task, t]);
  const milestoneDueMap = useMemo(() => task ? getMilestoneDueMap(task.planning) : new Map<string, string>(), [task]);

  // Whether there is anywhere to move the task to (the modal itself picks the target).
  // Only the PMO (admins) and the project's PMO accompanist move a task to another project.
  const canMigrate = !!task && canMigrateTask(isAdmin, user?.name, projects.find(p => p.id === task.project_id)?.ownership)
    && migrateTargets.some(p => p.id !== task.project_id);

  // A task may also belong to any number of other projects — offered by
  // number + name, excluding whichever project is currently the primary one.
  const linkedProjectOptions = useMemo(
    () => task ? projects.filter(p => p.id !== task.project_id).map(p => ({ value: p.id, label: `${projectDisplayCode(p).toUpperCase()} — ${p.name}` })) : [],
    [task, projects]
  );

  // The top badge shows whichever project this view was opened from (falling
  // back to the primary project without board context, e.g. a direct link or
  // notification); the "linked to" indicator shows every other one of the
  // task's projects. Both derive from the same list so the current project
  // can never also show up in "linked to" (and vice versa).
  const { currentProject, otherProjects } = useMemo(() => {
    if (!task) return { currentProject: null as { id: number; name: string; code: string | null } | null, otherProjects: [] as { id: number; name: string; code: string | null }[] };
    const all = [
      { id: task.project_id, name: task.project_name, code: task.project_code as string | null },
      ...task.linked_projects.map((lp) => ({ id: lp.id, name: lp.name, code: lp.project_code })),
    ];
    const effectiveContext = contextProjectId ?? task.project_id;
    const current = all.find((p) => p.id === effectiveContext) ?? all[0];
    return { currentProject: current, otherProjects: all.filter((p) => p.id !== current.id) };
  }, [task, contextProjectId]);

  // The linked-projects picker edits links *other than* whichever project
  // this view was opened from — that one is implicit (it's why the task is
  // here), so it's hidden from both the options and the current selection.
  const editableLinkedOptions = useMemo(
    () => linkedProjectOptions.filter((o) => o.value !== (currentProject?.id ?? -1)),
    [linkedProjectOptions, currentProject]
  );
  const editableLinkedSelected = useMemo(
    () => linkedProjectIds.filter((id) => id !== (currentProject?.id ?? -1)),
    [linkedProjectIds, currentProject]
  );

  if (loading) return <div className="page"><div className="spinner" /></div>;
  if (!task) return <div className="page"><p>{privateLocked ? t('tasks.privateLocked') : t('tasks.notFound')}</p><button className="btn btn-ghost" onClick={onBack}>{t('common.back')}</button></div>;

  const isOwner = task.created_by === currentUserId;
  // Editable only from its home (project_id) project — reached via a linked
  // project, it's a read-only copy so collaborators who don't share the same
  // team/context on the home project can't create conflicting edits.
  const isSource = currentProject?.id === task.project_id;

  // Every field edit writes the whole record (the server expects it), with the
  // changed field(s) on top of what the page holds.
  const persist = (patch: Record<string, unknown>) => api.updateTask(task.id, {
    title: title.trim() || task.title,
    description: description || null,
    due_date: dueDate || null,
    planned_at: dateInputToPlannedAtKeepingTime(plannedDate, task.planned_at),
    assignee_name: assigneeName || null,
    private_notes: privateNotes || null,
    milestone_id: milestoneId || null,
    linked_project_ids: linkedProjectIds,
    ...patch,
  }).then(() => loadTask());

  const projectCode = (currentProject?.code ?? task.project_code)?.toUpperCase();
  const prop = (label: React.ReactNode, body: React.ReactNode, extra?: React.ReactNode) => (
    <div className="td-prop">
      <div className="td-prop-label">{label}</div>
      <div className="td-prop-value">{body}{extra}</div>
    </div>
  );

  return (
    <div className="page project-page task-page">
      <button className="form-back pd-back" onClick={() => { window.location.hash = `tasks?project=${contextProjectId ?? task.project_id}`; }}>
        <ArrowLeft className="icon-sm" /> {t('tasks.backToBoard')}
      </button>

      {/* Masthead: what the task is and where it belongs, its tools, then its status. */}
      <header className="pd-masthead initiative-form">
        <div className="pd-masthead-top">
          <div className="pd-masthead-id">
            <div className="pd-eyebrow">
              <span>{t('tasks.taskLabel')}</span>
              {projectCode && <span className="badge">{projectCode}</span>}
              <a className="td-project-link" href={`#project?id=${currentProject?.id ?? task.project_id}`}>{currentProject?.name ?? task.project_name}</a>
              {isSource && otherProjects.length > 0 && (
                <span className="td-eyebrow-note" title={t('tasks.linkedProject')}>
                  <Link size={11} /> {t('tasks.presentInProjects', { projects: otherProjects.map((p) => p.code?.toUpperCase() || p.name).join(', ') })}
                </span>
              )}
            </div>
            <input
              className="form-input--bare bare-title pd-title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              onBlur={save}
              readOnly={!isSource}
              placeholder={t('tasks.titleBarePlaceholder')}
            />
          </div>
          <div className="pd-masthead-tools">
            <RemindTaskAssignee taskId={task.id} canRemind={task.can_remind} assigneeName={task.assignee_name} onSent={loadTask} />
            <ContactTaskCreator taskId={task.id} creatorId={task.created_by} creatorName={task.created_by_name} currentUserId={currentUserId} onSent={loadTask} />
            {isSource && canMigrate && (
              <button type="button" className="btn btn-ghost btn-icon" title={t('tasks.migrateTask')} aria-label={t('tasks.migrateTask')} onClick={() => setShowMigrate(true)}>
                <ArrowRightLeft size={14} />
              </button>
            )}
            {isSource && (
              <button type="button" className="btn btn-ghost btn-icon td-delete" title={t('tasks.deleteTask')} aria-label={t('tasks.deleteTask')} onClick={handleDelete}>
                <Trash2 size={14} />
              </button>
            )}
          </div>
        </div>
        <div className="td-status">
          <span className="td-status-label">{t('common.status')}</span>
          <div className="selectable-group">
            {columnOptions.map((c) => (
              <button key={c.value} type="button" className={`selectable${columnId === c.value ? ' active' : ''}`}
                disabled={!isSource} onClick={() => handleColumnChange(c.value)}>
                {c.label}
              </button>
            ))}
          </div>
        </div>
        {!isSource && (
          <div className="stage-next td-readonly-band">
            <div className="stage-next-body">
              <span className="stage-next-title"><Link size={12} /> {t('tasks.primaryProjectPrefix')} {task.project_code?.toUpperCase() || task.project_name}</span>
              <span className="stage-next-help">{t('tasks.readOnlyNotice')}</span>
            </div>
          </div>
        )}
      </header>

      <div className="pd-layout">
        <main className={`pd-main initiative-form${isSource ? '' : ' task-detail-readonly'}`}>
          <ProjectSection title={t('common.description')}>
            <BareTextarea value={description} onChange={setDescription} onBlur={save} placeholder={t('tasks.describePlaceholder')} />
          </ProjectSection>

          {/* [Proposition: vue-jalons-taches] To-do list */}
          <ProjectSection title={<>{t('vjt.todo.header')}{todoCount && todoCount.total > 0 ? <span className="td-count">{todoCount.done}/{todoCount.total}</span> : null}</>}>
            <TaskTodoList taskId={task.id} readOnly={!isSource} taskDone={doneColumnIds.has(task.column_id)} onChanged={loadTask} taskDueDate={task.due_date}
              onCounts={(done, total) => setTodoCount({ done, total })}
              onValidateTask={() => { const doneId = [...doneColumnIds][0]; if (doneId) handleColumnChange(doneId); }} />
          </ProjectSection>

          {/* [Proposition: documents] Files and links attached to the task. */}
          <ProjectSection title={<>{t('docs.titleTask')} <InfoTip>{t('docs.hintTask')}</InfoTip></>}>
            <DocumentsList target={{ type: 'task', id: task.id }} readOnly={!isSource} showHeader={false} />
          </ProjectSection>

          <ProjectSection title={t('tasks.dependencies')}>
            <div className="td-deps">
              {(['depends_on', 'enables'] as const).map((kind) => (
                <div key={kind} className="td-dep-group">
                  <div className="td-dep-head">
                    <span className="td-sub-label">{kind === 'depends_on' ? t('tasks.dependentOn') : t('tasks.enables')}</span>
                    <button type="button" className="btn btn-ghost btn-sm" onClick={() => openDepPicker(kind)}>
                      <Plus size={12} /> {t('common.add')}
                    </button>
                  </div>
                  <DepList deps={kind === 'depends_on' ? task.depends_on : task.enables} onRemove={removeDep} />
                </div>
              ))}
            </div>

            {showDepPicker && (
              <div className="task-dep-picker" style={{ marginTop: 'var(--s12)' }}>
                <div className="task-dep-picker-title">
                  {showDepPicker === 'depends_on' ? t('tasks.selectDependsOn') : t('tasks.selectEnables')}
                </div>
                {projectTasks.length > 0 ? (
                  <div className="task-dep-picker-list">
                    {projectTasks.map((pt) => (
                      <button key={pt.id} className="task-dep-picker-item" onClick={() => addDep(pt.id)}>
                        <Link size={12} />
                        <span style={{ flex: 1 }}>{pt.title}</span>
                        <span className="task-dep-picker-item-status">{pt.column_label}</span>
                      </button>
                    ))}
                  </div>
                ) : (
                  <div style={{ fontSize: 'var(--t-12)', color: 'var(--ink-3)' }}>{t('tasks.noOtherTasks')}</div>
                )}
                <div className="task-dep-picker-create">
                  <input
                    className="form-input"
                    style={{ flex: 1 }}
                    placeholder={t('tasks.createNewPlaceholder')}
                    value={newDepTitle}
                    onChange={(e) => setNewDepTitle(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && createAndAddDep()}
                  />
                  <input className="form-input" type="date" value={newDepPlanned} onChange={(e) => setNewDepPlanned(e.target.value)}
                    title={t('tasks.plannedDate')} aria-label={t('tasks.plannedDate')} />
                  <button className="btn btn-accent btn-sm" onClick={createAndAddDep} disabled={!newDepTitle.trim() || !newDepPlanned}
                    title={!newDepPlanned ? t('tlt.req.planned') : undefined}>{t('common.create')}</button>
                </div>
                <button className="btn btn-ghost btn-sm" onClick={() => setShowDepPicker(null)}>{t('common.cancel')}</button>
              </div>
            )}
          </ProjectSection>

          {/* Private Notes (owner only). [Proposition: vue-jalons-taches] For the proposals'
              reviewers the field is gone — notes go in the to-do items' "Description et suivi";
              an existing note stays readable until moved there. */}
          {isOwner && !canSeeProposals && (
            <ProjectSection title={t('tasks.privateNotes')}>
              <BareTextarea value={privateNotes} onChange={setPrivateNotes} onBlur={save} placeholder={t('tasks.privateNotesPlaceholder')} />
            </ProjectSection>
          )}
          {isOwner && canSeeProposals && privateNotes.trim() && (
            <ProjectSection title={<>{t('tasks.privateNotes')} <InfoTip>{t('vjt.legacyNotesHint')}</InfoTip></>}>
              <div className="legacy-notes">{privateNotes}</div>
            </ProjectSection>
          )}
        </main>

        <aside className="pd-side">
          {/* Who, when, where: one aligned list of the task's properties. */}
          <ProjectSection title={t('common.details')}>
            <div className={`td-props${isSource ? '' : ' task-detail-readonly'}`}>
              {prop(t('tasks.assignee'), (
                <DropdownSelect
                  value={assigneeName}
                  options={assigneeOptions}
                  placeholder={t('tasks.unassigned')}
                  onChange={(val) => { const name = String(val); setAssigneeName(name); persist({ assignee_name: name || null }); }}
                />
              ))}
              {prop(<>{t('collab.label')} <ProjectPeopleHint projectId={task.project_id} /></>, (
                <TaskCollaboratorsField
                  people={projectPeople(task)}
                  assignee={assigneeName}
                  value={task.collaborator_names ?? []}
                  onChange={(names) => { setTask({ ...task, collaborator_names: names }); persist({ collaborator_names: names }); }}
                />
              ))}
              {prop(t('tasks.milestone'), milestoneOptions.length > 0 ? (
                <div className="td-inline">
                  <DropdownSelect
                    value={milestoneId}
                    options={milestoneOptions}
                    placeholder={t('tasks.chooseMilestone')}
                    onChange={(val) => {
                      const mid = String(val);
                      // Clamp the due date to the milestone's estimated end
                      // (adopt when unset, pull back when later) — for everyone
                      // (issue #36). The server re-derives this authoritatively
                      // for non-admins on save, and loadTask() reconciles this
                      // optimistic value with whatever it persisted.
                      const newDue = clampDueToMilestone(dueDate, mid ? (milestoneDueMap.get(mid) || '') : '');
                      setMilestoneId(mid);
                      setDueDate(newDue);
                      persist({ milestone_id: mid || null, due_date: newDue || null });
                    }}
                  />
                  {milestoneId && (
                    <button type="button" className="btn btn-ghost btn-icon" title={t('tasks.openMilestone')} aria-label={t('tasks.openMilestone')} onClick={openMilestonePanel}>
                      <PanelRightOpen size={14} />
                    </button>
                  )}
                </div>
              ) : <NoMilestonesNotice projectId={task.project_id} existingTask />,
                milestoneOptions.length > 0 && !milestoneId ? <MissingMilestoneNotice /> : null)}
              {prop(t('tasks.dueDate'), (
                <input
                  className="form-input"
                  type="date"
                  value={dueDate}
                  min={task.created_at ? localDayOf(task.created_at) : undefined}
                  onChange={(e) => setDueDate(e.target.value)}
                  onBlur={save}
                  disabled={!isAdmin || !isSource}
                  title={!isSource ? t('tasks.readOnlyNotice') : isAdmin ? undefined : t('tasks.dueDateAdminOnly')}
                />
              ), <DateHistory history={task.due_date_history} />)}
              {prop(t('tasks.plannedDate'), (
                <input className="form-input" type="date" value={plannedDate} onChange={(e) => setPlannedDate(e.target.value)} onBlur={save} />
              ), <DateHistory history={task.planned_at_history} formatValue={formatPlannedSlot} />)}
              {prop(t('tasks.privacyLabel'), (
                <TaskPrivacyField taskId={task.id} isPrivate={!!task.is_private} canManage={isSource && !!task.can_manage_privacy} onChanged={() => loadTask()} />
              ))}
              {prop(<>{t('tasks.page.alsoIn')} <InfoTip>{t('tasks.page.alsoInHelp')}</InfoTip></>, (
                <div className="linked-projects-field">
                  <MultiSelectFilter
                    label=""
                    options={editableLinkedOptions}
                    selected={editableLinkedSelected}
                    onChange={changeLinkedProjects}
                    placeholder={t('tasks.linkedProjectPlaceholder')}
                  />
                </div>
              ))}
              {prop(t('tasks.page.createdBy'), (
                <span className="td-prop-text">{task.created_by_name}{task.created_at ? ` · ${formatMilestoneDueDate(localDayOf(task.created_at))}` : ''}</span>
              ))}
            </div>
          </ProjectSection>

          <section className="pd-card pd-side-card">
            <header className="pd-card-head"><h3 className="pd-card-title">{t('taskTimeline.updates')}</h3></header>
            <div className="pd-card-body">
              <TaskTimeline
                updates={task.updates}
                onPost={postUpdate}
                onEdit={editUpdate}
                onDelete={deleteUpdate}
                currentUserId={currentUserId}
                isAdmin={isAdmin}
                taskId={taskId}
                projectId={task.project_id}
                onRefresh={loadTask}
                hideHeader
              />
            </div>
          </section>
        </aside>
      </div>

      <ConfirmModal
        open={confirmDelete}
        title={t('tasks.deleteTask')}
        message={t('tasks.deleteConfirm')}
        confirmLabel={t('common.delete')}
        destructive
        onConfirm={handleDeleteConfirm}
        onCancel={() => setConfirmDelete(false)}
      />

      {showMigrate && task && (
        <MigrateTaskModal
          taskId={task.id}
          fromProjectId={task.project_id}
          projects={migrateTargets}
          onClose={() => setShowMigrate(false)}
          onMigrated={() => { setShowMigrate(false); loadTask(); }}
        />
      )}

      {milestonePanelTasks && milestoneId && (
        <MilestoneDetailPanel
          project={milestoneProject}
          milestoneId={milestoneId}
          tasks={milestonePanelTasks}
          doneColumnIds={closedColumnIds}
          blockedColumnIds={blockedColumnIds}
          onClose={() => setMilestonePanelTasks(null)}
        />
      )}
    </div>
  );
}

function DepList({ deps, onRemove }: { deps: TaskDependency[]; onRemove: (depId: number) => void }) {
  const { t } = useT();
  if (deps.length === 0) {
    return <div className="text-ink-3" style={{ fontSize: 'var(--t-12)' }}>{t('common.none')}</div>;
  }
  return (
    <div className="task-dep-list">
      {deps.map((d) => (
        <div key={d.dep_id} className="task-dep-item">
          <span className="task-dep-item-title">{d.title}</span>
          <span className="task-dep-item-status">{d.column_label}</span>
          <button className="btn btn-ghost btn-sm" style={{ padding: '2px' }} onClick={() => onRemove(d.dep_id)}>
            <X size={12} />
          </button>
        </div>
      ))}
    </div>
  );
}

// ── Main Page ──

export function TasksPage() {
  const { t } = useT();
  const { user } = useAuth();
  const { canSeeAllProjects, projectIds, myTaskNames } = useRole();
  const [columns, setColumns] = useState<GridColumn[]>([]);
  const [board, setBoard] = useState<KanbanBoard>({ projects: [], tasks: [] });
  const [allProjects, setAllProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);

  // Filters — multi-select projects / sponsor / collaborators (client-side)
  // Seeded from a `project=<id>` hash param when arriving from a project's
  // "Task board →" link, so that project is preselected (removable after).
  // Shared with Planification des tâches (PageFiltersContext): the selection stays when switching.
  const { taskFilterProjects: filterProjects, setTaskFilterProjects, taskFilterPersons: filterPersons, setTaskFilterPersons } = usePageFilters();
  const projectFilter = useProjectFilter();
  const personFilter = usePersonFilter();
  // Arriving from a project's "Kanban du projet" button (or a person link): that selection wins.
  useEffect(() => {
    const p = new URLSearchParams(window.location.hash.split('?')[1] || '');
    const proj = p.get('project'), person = p.get('person');
    if (proj) { setTaskFilterProjects([Number(proj)]); setTaskFilterPersons([]); }
    if (person) setTaskFilterPersons([person]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  // The project we arrived from (if any) — lets us offer a deterministic way
  // back to that project's overview (issue #40), rather than relying solely
  // on the browser's back button.
  const [originProjectId] = useState<number | null>(() => {
    const p = new URLSearchParams(window.location.hash.split('?')[1] || '');
    const proj = p.get('project');
    return proj ? Number(proj) : null;
  });

  // Off by default (issue #29 dimmed every non-own task unconditionally,
  // which buried the board for anyone reviewing others' work) — greying
  // out what isn't "mine" is now opt-in via this toggle.
  const [highlightMine, setHighlightMine] = useState(false);

  // Filter options
  const [domains, setDomains] = useState<GridColumn[]>([]);
  const [levels, setLevels] = useState<GridRow[]>([]);
  const [statuses, setStatuses] = useState<GridColumn[]>([]);

  // UI state
  const [panelTask, setPanelTask] = useState<{ id: number; contextProjectId: number } | null>(null);  // side panel (info)
  const [detailTaskId, setDetailTaskId] = useState<number | null>(() => {
    const p = new URLSearchParams(window.location.hash.split('?')[1] || '');
    const taskParam = p.get('task');
    return taskParam ? Number(taskParam) : null;
  });
  // The board row (project) the detail view was opened from — lets the
  // "linked project" indicator show whichever of the task's two projects
  // isn't the one the user navigated from.
  const [detailContextProjectId, setDetailContextProjectId] = useState<number | null>(() => {
    const p = new URLSearchParams(window.location.hash.split('?')[1] || '');
    const from = p.get('from');
    return from ? Number(from) : null;
  });
  // The create-task dialog: from the toolbar button (nothing preset) or a cell's "+" (its project and column).
  const [createIn, setCreateIn] = useState<{ projectId?: number; columnId?: number } | null>(null);
  const [activeTask, setActiveTask] = useState<Task | null>(null);

  // Projects that can receive tasks — exclude Inbox-stage (intake candidates)
  // and Done/Closed projects from the Create Task picker.
  const taskableProjects = useMemo(() => {
    const inbox = getInboxStatusIds(statuses);
    const done = getDoneStatusIds(statuses);
    return allProjects.filter(p => {
      if (inbox.has(p.status_id) || done.has(p.status_id)) return false;
      // Non-admin/direction users can only task their own projects
      if (!canSeeAllProjects && !projectIds.includes(p.id)) return false;
      return true;
    });
  }, [allProjects, statuses, canSeeAllProjects, projectIds]);
  const taskableIds = useMemo(() => new Set(taskableProjects.map(p => p.id)), [taskableProjects]);

  // Columns named "Blocked" (admin-configured) — tasks sitting there show/edit
  // a reason inline on the card (issue #39).
  const blockedColumnIds = useMemo(() => getBlockedColumnIds(columns), [columns]);

  // Names on the board's cards, for the person filter.
  const boardPeople = useMemo(() => board.tasks.flatMap(bt => [bt.assignee_name ?? '', ...(bt.collaborator_names ?? [])]), [board.tasks]);
  const personMatch = useCallback((bt: Task) => personFilter([bt.assignee_name, ...(bt.collaborator_names ?? [])]), [personFilter]);

  const visibleBoardProjects = useMemo(() => board.projects.filter(bp => {
    // Scope to the logged-in user's own projects (admin/direction see all)
    if (!canSeeAllProjects && !projectIds.includes(bp.id)) return false;
    if (!projectFilter(allProjects.find(p => p.id === bp.id), bp.id)) return false;
    // With a person selected, only projects where they have a card.
    if (filterPersons.length && !board.tasks.some(bt => (bt.project_id === bp.id || bt.linked_projects.some(lp => lp.id === bp.id)) && personMatch(bt))) return false;
    return true;
  }), [board.projects, board.tasks, allProjects, projectFilter, filterPersons, personMatch, canSeeAllProjects, projectIds]);

  // Drag sensors — require some movement before drag starts (to allow title clicks)
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } })
  );

  const loadBoard = useCallback(async () => {
    const data = await api.getKanbanBoard() as KanbanBoard;
    setBoard(data);
  }, []);

  // Live: who else is on the board (and which card they move or have open);
  // someone else's save refetches the board — after this user's own drag, if
  // one is under way, so the card doesn't jump from under the pointer.
  const livePeople = usePresence('tasks-board', user?.id);
  const liveFocusByTask = useMemo(() => focusMap(livePeople, 'task'), [livePeople]);
  const dragging = useRef(false);
  const reloadAfterDrag = useRef(false);
  useLiveChanges((c) => {
    if (c.area === 'kanban') return;
    if (dragging.current) reloadAfterDrag.current = true;
    else loadBoard();
  });
  useEffect(() => {
    const id = detailTaskId ?? panelTask?.id ?? null;
    setLiveFocus(id ? { type: 'task', id, action: 'open' } : null);
  }, [detailTaskId, panelTask?.id]);

  const saveBlockedReason = useCallback((taskId: number, reason: string) => {
    setBoard(prev => ({
      ...prev,
      tasks: prev.tasks.map(t => t.id === taskId ? { ...t, blocked_reason: reason || null } : t),
    }));
    api.setTaskBlockedReason(taskId, reason || null).catch(() => { loadBoard(); });
  }, [loadBoard]);

  const loadMeta = useCallback(async () => {
    const [cols, doms, levs, stats, projs] = await Promise.all([
      api.getKanbanColumns() as Promise<GridColumn[]>,
      api.getGridColumns('portfolio') as Promise<GridColumn[]>,
      api.getGridRows() as Promise<GridRow[]>,
      api.getGridColumns('pipeline') as Promise<GridColumn[]>,
      api.getProjects() as Promise<Project[]>,
    ]);
    setColumns(cols);
    setDomains(doms);
    setLevels(levs);
    setStatuses(stats);
    setAllProjects(projs);
  }, []);

  useEffect(() => {
    loadMeta().then(() => setLoading(false));
  }, [loadMeta]);

  useEffect(() => {
    if (!loading) loadBoard();
  }, [loadBoard, loading]);

  // Sync detail view with URL hash so browser back works
  useEffect(() => {
    const onHashChange = () => {
      const raw = window.location.hash;
      if (!raw.startsWith('#tasks')) return;
      const p = new URLSearchParams(raw.split('?')[1] || '');
      const taskParam = p.get('task');
      const id = taskParam ? Number(taskParam) : null;
      setDetailTaskId(id);
      const from = p.get('from');
      setDetailContextProjectId(from ? Number(from) : null);
      if (!id) loadBoard();
    };
    window.addEventListener('hashchange', onHashChange);
    return () => window.removeEventListener('hashchange', onHashChange);
  }, [loadBoard]);

  // ── Drag handlers ──

  const handleDragStart = (event: DragStartEvent) => {
    const task = event.active.data.current?.task as Task;
    if (task) {
      setActiveTask(task);
      dragging.current = true;
      setLiveFocus({ type: 'task', id: task.id, action: 'drag' });
    }
  };

  const handleDragEnd = (event: DragEndEvent) => {
    setActiveTask(null);
    dragging.current = false;
    const dropped = event.active.data.current?.task as Task | undefined;
    if (dropped && event.over && String(event.over.id) !== `cell-${dropped.project_id}-${dropped.column_id}`) flashMoved('task', dropped.id);
    else setLiveFocus(null);
    if (reloadAfterDrag.current) { reloadAfterDrag.current = false; setTimeout(loadBoard, 600); }
    const { active, over } = event;
    if (!over) return;

    const task = active.data.current?.task as Task;
    if (!task) return;

    // Parse droppable id: "cell-{projectId}-{columnId}"
    const overId = String(over.id);
    if (!overId.startsWith('cell-')) return;

    const parts = overId.split('-');
    const targetProjectId = Number(parts[1]);
    const targetColumnId = Number(parts[2]);

    // Only the task's home row is a valid column-move target — dragging is
    // already disabled from linked (non-home) rows, but this also blocks
    // dropping a home-row drag onto a linked row's cell.
    if (targetProjectId !== task.project_id) return;

    // Same column = no-op
    if (targetColumnId === task.column_id) return;

    // Optimistic update
    setBoard((prev) => ({
      ...prev,
      tasks: prev.tasks.map((bt) =>
        bt.id === task.id ? { ...bt, column_id: targetColumnId } : bt
      ),
    }));

    // Persist
    api.moveTask(task.id, targetColumnId).catch((err: Error & { code?: string }) => {
      // Revert on failure
      loadBoard();
      // [Proposition: vue-jalons-taches] Done refused while the to-do list isn't fully checked.
      if (/to-do/i.test(err.message)) alert(t('vjt.task.doneRefused'));
      else if (err.code === 'TASK_STATUS_FORBIDDEN') alert(t('tasks.statusForbidden'));
    });
  };

  if (loading) {
    return <div style={{ padding: 'var(--s24)', color: 'var(--ink-3)' }}>{t('common.loading')}</div>;
  }

  if (columns.length === 0) {
    return (
      <div style={{ padding: 'var(--s24)' }}>
        <div className="empty-state">
          <div className="empty-state-title">{t('tasks.noColumnsTitle')}</div>
          <div className="empty-state-desc">{t('tasks.noColumnsDesc')}</div>
        </div>
      </div>
    );
  }

  // ── Full detail page view ──
  if (detailTaskId && user) {
    return (
      <TaskDetailView
        taskId={detailTaskId}
        currentUserId={user.id}
        columns={columns}
        projects={allProjects}
        migrateTargets={taskableProjects}
        contextProjectId={detailContextProjectId}
        onBack={() => history.back()}
        onDeleted={() => { history.replaceState(null, '', '#tasks'); setDetailTaskId(null); loadBoard(); }}
      />
    );
  }

  const totalCols = columns.length;

  return (
    <div className="grid-page">
      <LiveCursors people={livePeople} />
      {/* Filter toolbar — filters left, page actions right (same layout on every page) */}
      <div className="toolbar page-toolbar">
        <div className="toolbar-filters">
          <TaskFilters projects={allProjects} people={boardPeople}>
            <FilterToggle label={t('tasks.highlightMine')} checked={highlightMine} onChange={setHighlightMine} />
          </TaskFilters>
        </div>
        <div className="toolbar-actions">
          <PresenceAvatars people={livePeople} />
          {originProjectId != null && (
            <a className="btn btn-ghost btn-sm" href={`#project?id=${originProjectId}`}>
              <ArrowLeft size={14} /> {t('tasks.backToProject')}
            </a>
          )}
          {/* Filtered to one project (e.g. arriving from it): the new task goes there by default. */}
          <button className="btn btn-accent btn-sm" onClick={() => setCreateIn({
            projectId: filterProjects.length === 1 && taskableProjects.some(p => p.id === Number(filterProjects[0])) ? Number(filterProjects[0]) : undefined,
          })}>
            <Plus size={14} /> {t('tasks.createTaskBtn')}
          </button>
        </div>
      </div>

      {/* Board */}
      {board.projects.length === 0 ? (
        <div style={{ padding: 'var(--s24)' }}>
          <div className="empty-state">
            <div className="empty-state-title">{t('tasks.emptyTitle')}</div>
            <div className="empty-state-desc">{t('tasks.emptyDesc')}</div>
          </div>
        </div>
      ) : (
        <DndContext sensors={sensors} onDragStart={handleDragStart} onDragEnd={handleDragEnd}
          onDragOver={(e) => { const tk = e.active.data.current?.task as Task | undefined; if (tk) setLiveFocus({ type: 'task', id: tk.id, action: 'drag', ...(e.over ? { over: String(e.over.id) } : {}) }); }}
          onDragCancel={() => { setActiveTask(null); dragging.current = false; setLiveFocus(null); }}>
          <div className="matrix-grid-container">
            <div
              className="matrix-grid"
              style={{ gridTemplateColumns: `120px repeat(${totalCols}, 316px)` }}
            >
              <div className="matrix-grid-corner" />

              {columns.map((col) => (
                <div key={col.id} className="matrix-grid-col">{col.label}</div>
              ))}

              {visibleBoardProjects.map((project) => (
                <>
                  <div key={`row-${project.id}`} className="matrix-grid-row kanban-row-label">
                    <button
                      type="button"
                      className="kanban-row-link"
                      title={t('tasks.backToProject')}
                      onClick={() => { window.location.hash = `project?id=${project.id}`; }}
                    >
                      {project.name}
                    </button>
                    <span className="kanban-row-code">{projectDisplayCode(project).toUpperCase()}</span>
                  </div>
                  {columns.map((col) => {
                    // Own tasks first, others below — greyed out only when the
                    // "Mes tâches" toggle is on. "mine" matched the same way My
                    // Tasks does (account name or linked person name,
                    // case-insensitive). A task also shows on every linked
                    // project's row too, but is only editable/draggable on its
                    // home (project_id) row — elsewhere it's a read-only copy,
                    // badged with the home project's code instead.
                    const cellTasks = board.tasks
                      .filter((bt) => (bt.project_id === project.id || bt.linked_projects.some((lp) => lp.id === project.id)) && bt.column_id === col.id && personMatch(bt))
                      .map((bt) => {
                        const isSource = bt.project_id === project.id;
                        const linkInfo: TaskLinkInfo | undefined = isSource
                          ? (bt.linked_projects.length > 0
                              ? { kind: 'alsoIn', codes: bt.linked_projects.map((lp) => lp.project_code?.toUpperCase() || lp.name) }
                              : undefined)
                          : (() => {
                              const home = board.projects.find((p) => p.id === bt.project_id);
                              return { kind: 'source', code: home ? projectDisplayCode(home).toUpperCase() : String(bt.project_id) };
                            })();
                        return { task: bt, isSource, isMine: (!!bt.assignee_name && myTaskNames.has(bt.assignee_name.toLowerCase())) || (bt.collaborator_names ?? []).some((n) => myTaskNames.has(n.toLowerCase())), linkInfo };
                      })
                      .sort((a, b) => Number(b.isMine) - Number(a.isMine));
                    const isBlockedColumn = blockedColumnIds.has(col.id);
                    return (
                      <DroppableCell key={`cell-${project.id}-${col.id}`} id={`cell-${project.id}-${col.id}`}>
                        {draggingOver(livePeople, 'task', `cell-${project.id}-${col.id}`).map((p) => {
                          const ghost = board.tasks.find((x) => x.id === p.focus!.id);
                          return ghost && !(ghost.project_id === project.id && ghost.column_id === col.id) && <LiveGhost key={`ghost-${p.userId}`} person={p}><TaskCard task={ghost} onTitleClick={() => {}} /></LiveGhost>;
                        })}
                        {cellTasks.map(({ task, isSource, isMine, linkInfo }) => (
                          <DraggableTaskCard
                            key={task.id}
                            task={task}
                            locked={!isSource || task.can_open === false}
                            dimmed={highlightMine && !isMine}
                            linkInfo={linkInfo}
                            onTitleClick={() => {
                              if (task.can_open === false) { alert(t('tasks.privateLocked')); return; }
                              window.location.hash = `tasks?task=${task.id}&from=${project.id}`;
                            }}
                            onInfo={(e) => {
                              e.stopPropagation();
                              if (task.can_open === false) { alert(t('tasks.privateLocked')); return; }
                              setPanelTask({ id: task.id, contextProjectId: project.id });
                            }}
                            isBlocked={isBlockedColumn}
                            liveBy={liveFocusByTask.get(task.id)}
                            onBlockedReasonChange={isBlockedColumn && isSource && task.can_open !== false ? (reason) => saveBlockedReason(task.id, reason) : undefined}
                          />
                        ))}
                        {taskableIds.has(project.id) && (
                          <button type="button" className="kanban-cell-add" onClick={() => setCreateIn({ projectId: project.id, columnId: col.id })}
                            title={t('tasks.createTask')} aria-label={t('tasks.createTask')}>
                            <Plus size={14} />
                          </button>
                        )}
                      </DroppableCell>
                    );
                  })}
                </>
              ))}
            </div>
          </div>

          {/* Drag overlay */}
          <DragOverlay>
            {activeTask && (
              <div data-dnd-overlay>
                <TaskCard task={activeTask} onTitleClick={() => {}} />
              </div>
            )}
          </DragOverlay>
        </DndContext>
      )}

      {/* Task info side panel */}
      {panelTask && user && (
        <TaskDetailPanel
          taskId={panelTask.id}
          contextProjectId={panelTask.contextProjectId}
          currentUserId={user.id}
          onClose={() => setPanelTask(null)}
          onOpenDetail={() => { const p = panelTask; setPanelTask(null); window.location.hash = `tasks?task=${p.id}&from=${p.contextProjectId}`; }}
          onDeleted={loadBoard}
          onUpdated={loadBoard}
        />
      )}

      {/* Create task modal */}
      {createIn && (
        <CreateTaskModal
          key={`${createIn.projectId ?? ''}-${createIn.columnId ?? ''}`}
          columns={columns}
          projects={taskableProjects}
          defaultProjectId={createIn.projectId}
          defaultColumnId={createIn.columnId}
          onClose={() => setCreateIn(null)}
          onCreated={loadBoard}
        />
      )}
    </div>
  );
}
